import { camelize, camelizeKeys } from 'humps'
import omit from 'lodash.omit'
import { normalize, schema } from 'normalizr'
import WPAPI from 'wpapi'

import { camelize as camelizeResponse } from 'api'

import routes from '../schema/default-routes'
import transport from './http-transport'
import { schemaFromKind } from 'wordpress/schemas'

/* WP API & custom routes */
const api = new WPAPI({ endpoint: CMS_API_URL, routes, transport })

/* Configuration (/config) */

// Helpers
const entityFor = (name, options = {}) =>
    new schema.Entity(
        name,
        {},
        {
            idAttribute: (_, __, key) => key,
            ...options,
        }
    )

// Brands
const brandsSchema = new schema.Values(
    entityFor('brands', {
        processStrategy: value => camelizeKeys(value),
    })
)

// Components
const stripFields = component => omit(component, ['id', 'slug', 'layout', 'lang'])

const processContent = component => {
    if (!component) return

    const { content } = component

    if (!(content instanceof Array)) return component

    const properties = {}

    content.forEach(item => {
        const key = camelize(item.layout)
        const value = stripFields(processContent(item))

        if (properties[key] instanceof Array) properties[key].push(value)
        else if (properties[key]) {
            properties[`${key}s`] = [properties[key], value]

            delete properties[key]
        } else properties[key] = value
    })

    return {
        ...component,
        ...properties,
    }
}

const componentSchema = entityFor('components', {
    idAttribute: value => camelize(value.layout),
    processStrategy: compose(camelizeKeys, stripFields, processContent),
})

// Dashboard
const dashboardSchema = new schema.Values(
    entityFor('dashboard', {
        idAttribute: value => camelize(value.prop),
        processStrategy: value => camelizeKeys(value.value),
    })
)

// Languages
const processLanguages = (lang, _, key) => {
    if (lang.current) return { ...lang, current: key }
    return { ...lang }
}

const languages = entityFor('languages', {
    processStrategy: compose(camelizeKeys, processLanguages),
})
const languageSchema = new schema.Values(languages)

// Meta
const metafield = entityFor('meta', {
    idAttribute: value => value.name,
    processStrategy: value => value.value,
})
const metafieldsSchema = new schema.Values(metafield)

// Routes
const routesSchema = entityFor('routes', {
    idAttribute: value => value.slug,
})

// Configuration schema
export const configurationResponseSchema = {
    brands: brandsSchema,
    dashboard: dashboardSchema,
    languages: languageSchema,
    meta: metafieldsSchema,
    routes: [routesSchema],
    widgets: [componentSchema],
}

export const getConfiguration = language => {
    const config = api.config()

    if (language) config.param('lang', language)

    return config.then(json => normalize(json, configurationResponseSchema))
}

/* Posts (/:kind) */

const postsProcessor = json =>
    Object.values(json)
        .filter(post => post.id !== undefined)
        .map(post => omit(post, '_links'))

const pagingProcessor = (paging = {}) => {
    const processedPaging = {}

    Object.keys(omit(paging, ['_links'])).forEach(key => {
        processedPaging[key] = parseInt(paging[key])
    })

    return processedPaging
}

export const fetchPosts = (kind, postIDs, language = 'en') =>
    api[kind]()
        .param('include', postIDs)
        .param('lang', language)
        .then(camelizeResponse)
        .then(json => postsProcessor(json))
        .then(result => normalize(result, [schemaFromKind(kind)]))

export const queryPosts = (kind, params) => {
    const query = api[kind]()

    Object.keys(params).forEach(v => {
        query.param(v, params[v])
    })

    return query
        .then(json => ({
            posts: postsProcessor(json),
            paging: pagingProcessor(json._paging),
        }))
        .then(camelizeResponse)
        .then(result => normalize(result, { posts: [schemaFromKind(kind)] }))
}

/* Feedback */

export const sendFeedback = data => api.feedback().create({ ...data })

/* Newsletter subscription */

export const subscribeNews = email => api.subscribeNews().create({ email })

/* Offers subscription */

export const subscribeOffers = email => api.subscribeOffers().create({ email })

/* Custom form submission */

export const submitForm = data => api.form().create({ ...data })
